import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ttp-admin-token";

const AdminContext = createContext(null);

export function AdminProvider({ children }) {
  const [token, setToken] = useState(() => {
    try { return window.localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
  });
  const [admin, setAdmin] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | checking | authed | guest

  const persistToken = useCallback((value) => {
    setToken(value);
    try {
      if (value) window.localStorage.setItem(TOKEN_KEY, value);
      else window.localStorage.removeItem(TOKEN_KEY);
    } catch { /* ignore quota / privacy errors */ }
  }, []);

  const verify = useCallback(async (tkn) => {
    if (!tkn) {
      setAdmin(null);
      setStatus("guest");
      return false;
    }
    setStatus("checking");
    try {
      const { data } = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${tkn}` },
      });
      setAdmin(data);
      setStatus("authed");
      return true;
    } catch {
      persistToken(null);
      setAdmin(null);
      setStatus("guest");
      return false;
    }
  }, [persistToken]);

  useEffect(() => { verify(token); /* run once on mount + when token changes */ }, [token, verify]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/admin/login`, { email, password });
    persistToken(data.access_token);
    setAdmin({ email: data.email, role: "admin" });
    setStatus("authed");
    return data;
  }, [persistToken]);

  const logout = useCallback(() => {
    persistToken(null);
    setAdmin(null);
    setStatus("guest");
  }, [persistToken]);

  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  const value = useMemo(
    () => ({ token, admin, status, login, logout, authHeaders, api: API }),
    [token, admin, status, login, logout, authHeaders],
  );

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

export function useAdmin() {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error("useAdmin must be used within AdminProvider");
  return ctx;
}
